import { Injectable } from '@angular/core';
import { Observable, of, delay, throwError } from 'rxjs';
import { 
  UserRegistration, 
  PersonalData, 
  PersonalAddress, 
  BusinessData, 
  LoanUse, 
  PersonalReference, 
  AdditionalData, 
  DocumentUpload, 
  CreditHistory, 
  ApiResponse, 
  FormSubmissionResponse 
} from '../models/form-models';
import { LoanDetails } from '../models/form-models';

@Injectable({
  providedIn: 'root'
})
export class MockApiService {
  private readonly DELAY_MS = 1500; // Simular latencia de red
  private readonly SUCCESS_RATE = 0.9; // 90% de éxito

  // Simular registro de usuario
  registerUser(data: UserRegistration): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Usuario registrado exitosamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de datos personales
  submitPersonalData(data: PersonalData): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Datos personales guardados correctamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de dirección personal
  submitPersonalAddress(data: PersonalAddress): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Dirección guardada correctamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de datos de negocio
  submitBusinessData(data: BusinessData): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Datos de negocio guardados correctamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de uso del préstamo
  submitLoanUse(data: LoanUse): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Información del préstamo guardada correctamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de referencias personales
  submitPersonalReference(data: PersonalReference, referenceNumber: number): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: `Referencia personal ${referenceNumber} guardada correctamente`,
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de datos adicionales
  submitAdditionalData(data: AdditionalData): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Datos adicionales guardados correctamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular subida de documentos
  uploadDocuments(data: DocumentUpload): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Documentos subidos correctamente',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular envío de consentimiento de historial crediticio
  submitCreditHistory(data: CreditHistory): Observable<ApiResponse<FormSubmissionResponse>> {
    return this.simulateApiCall({
      success: true,
      message: 'Consentimiento de historial crediticio registrado',
      data: {
        id: this.generateId(),
        status: 'pending',
        submittedAt: new Date().toISOString()
      }
    });
  }

  // Simular verificación OTP
  verifyOtp(code: string): Observable<ApiResponse<{ verified: boolean }>> {
    const isValid = code === '123456'; // Código mock válido
    return this.simulateApiCall({
      success: isValid,
      message: isValid ? 'Código verificado correctamente' : 'Código inválido',
      data: { verified: isValid }
    });
  }

  // Método privado para simular llamadas API
  private simulateApiCall<T>(response: ApiResponse<T>): Observable<ApiResponse<T>> {
    const shouldSucceed = Math.random() < this.SUCCESS_RATE;
    
    if (!shouldSucceed) {
      return throwError(() => ({
        success: false,
        message: 'Error de conexión simulado',
        errors: ['Error de red simulado para testing']
      })).pipe(delay(this.DELAY_MS));
    }
    
    return of(response).pipe(delay(this.DELAY_MS));
  }

  // Método privado para generar delay aleatorio
  private getRandomDelay(): number {
    return Math.random() * 1000 + 500; // Entre 500ms y 1500ms
  }

  // Generar ID único
  private generateId(): string {
    return 'mock_' + Math.random().toString(36).substr(2, 9);
  }

  // Nuevo método para obtener detalles del préstamo
  getLoanDetails(amount: number, weeks: number = 16): Observable<ApiResponse<LoanDetails>> {
    return new Observable(observer => {
      setTimeout(() => {
        if (Math.random() > 0.1) { // 90% de éxito
          const interestRate = 0.15; // 15% de interés
          const totalWithInterest = amount * (1 + interestRate);
          const paymentAmount = totalWithInterest / weeks;
          
          // Calcular próxima fecha de pago
          const nextDate = new Date();
          nextDate.setDate(nextDate.getDate() + 7);
          const day = nextDate.getDate().toString().padStart(2, '0');
          const month = (nextDate.getMonth() + 1).toString().padStart(2, '0');
          const year = nextDate.getFullYear();
          
          const loanDetails: LoanDetails = {
            paymentAmount: paymentAmount,
            weeklyTerms: weeks,
            nextPaymentDate: `${day}/${month}/${year}`,
            totalWithInterest: totalWithInterest
          };
          
          observer.next({
            success: true,
            message: 'Detalles del préstamo obtenidos exitosamente',
            data: loanDetails
          });
        } else {
          observer.error({
            success: false,
            message: 'Error al obtener detalles del préstamo'
          });
        }
        observer.complete();
      }, this.getRandomDelay());
    });
  }
}